import React, { useState, useEffect } from 'react';

interface JobSource {
  id: number;
  name: string;
  display_name: string;
  corporate_name: string | null;
  url: string;
  logo_url: string | null;
  thumbnail_url: string | null;
  description: string | null;
  job_listings_count: number;
  partnership_type: string;
  payment_info: string | null;
  contract_type_name: string | null;
  is_applied: boolean;
}

interface FavoritedJobSource {
  id: number;
  job_source: JobSource;
  created_at: string;
}

interface FavoritedJobSourcesPageProps {
  initialJobSources?: JobSource[];
  isUserSignedIn?: boolean;
  currentUserId?: number;
  isProfileComplete?: boolean;
}

const FavoritedJobSourcesPage: React.FC<FavoritedJobSourcesPageProps> = ({
  initialJobSources = [],
  isUserSignedIn = false,
  currentUserId,
  isProfileComplete = false
}) => {
  const [jobSources, setJobSources] = useState<JobSource[]>(initialJobSources);
  const [isLoading, setIsLoading] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string>('');

  // CSRFトークンを取得
  useEffect(() => {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    setCsrfToken(token);
  }, []);

  // コンポーネントマウント時にデータを取得
  useEffect(() => {
    // ログイン状態に関係なくデータを取得
    fetchFavoritedJobSources();
  }, [isUserSignedIn]);

  // 保存済みエージェント一覧を取得
  const fetchFavoritedJobSources = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/mypage/favorited_job_sources.json', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // APIレスポンスの形式に応じてデータを変換
        const favorites = data.favorited_job_sources || [];
        setJobSources(favorites.map((favorite: FavoritedJobSource) => favorite.job_source));
      } else {
        console.error('Failed to fetch favorited job sources:', response.status);
        if (window.showGlobalFlash) {
          window.showGlobalFlash('保存済みエージェント一覧の取得に失敗しました', 'error', false);
        }
      }
    } catch (error) {
      console.error('Error fetching favorited job sources:', error);
      if (window.showGlobalFlash) {
        window.showGlobalFlash('保存済みエージェント一覧の取得に失敗しました', 'error', false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 応募ボタンのクリックハンドラ
  const handleApplyClick = (jobSource: JobSource) => {
    if (!isUserSignedIn) {
      // 未ログイン時: 応募サインアップページに遷移（job_source_idをパラメーターに）
      const params = new URLSearchParams({
        job_source_id: jobSource.id.toString(),
        action_type: 'apply'
      });
      window.location.href = `/apply/sign_up?${params.toString()}`;
    } else {
      // ログイン時: 直接応募処理を実行（POSTリクエスト）
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      fetch(`/job_sources/${jobSource.id}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      }).then(response => {
        if (response.redirected) {
          window.location.href = response.url;
        } else if (response.ok) {
          // 応募成功時は応募一覧ページに遷移
          window.location.href = '/mypage/applied_job_listings';
        } else {
          console.error('Failed to apply:', response.status, response.statusText);
          if (window.showGlobalFlash) {
            window.showGlobalFlash('応募に失敗しました', 'error', false);
          }
        }
      }).catch(error => {
        console.error('Error applying:', error);
        if (window.showGlobalFlash) {
          window.showGlobalFlash('応募に失敗しました', 'error', false);
        }
      });
    }
  };

  // ローディング中の表示
  if (isLoading) {
    return (
      <div className="container p-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">読み込み中...</span>
          </div>
        </div>
      </div>
    );
  }

  // 保存済みエージェントが空の場合
  if (!isLoading && jobSources.length === 0) {
    return (
      <div className="container p-4">
        <div className="text-center py-5">
          <i className="bi bi-bookmark-star fs-1 text-muted mb-3 d-block"></i>
          <h1 className="h3 fw-bold mb-3">保存済みエージェント一覧</h1>
          <p className="text-muted mb-4">
            保存されたエージェントはありません<br />
            気になるエージェントを保存すると、ここに表示されます
          </p>
          <a href="/job_sources" className="btn btn-primary">
            <i className="bi bi-search me-2"></i>エージェントを探す
          </a>
        </div>
      </div>
    );
  }

  // 保存済みエージェント一覧の表示
  return (
    <div className="container p-4">
      <h1 className="h3 fw-bold mb-4">保存済みエージェント一覧 ({jobSources.length}件)</h1>
      <div className="mb-5">
        {jobSources.map((jobSource) => (
          <div key={jobSource.id} className="card mb-4 border-0 shadow-sm">
            <div className="card-body p-4">
              {/* スマートフォン表示 */}
              <div className="d-block d-md-none mb-3">
                {/* ロゴ部分 */}
                <div className="mb-3 job-source-logo">
                  <a href={jobSource.url}>
                    {jobSource.logo_url ? (
                      <img
                        alt={`${jobSource.display_name}のロゴ`}
                        className="img-fluid border rounded"
                        style={{ maxHeight: '80px', padding: '4px' }}
                        loading="lazy"
                        decoding="async"
                        src={jobSource.logo_url}
                      />
                    ) : (
                      <div className="bg-light p-3 text-center border rounded">
                        <span className="text-muted">ロゴなし</span>
                      </div>
                    )}
                  </a>
                </div>
                
                {/* サービス名部分 */}
                <div className="mb-3">
                  <h3 className="h4 mb-1 fw-bold">
                    <a className="text-decoration-none text-dark" href={jobSource.url}>
                      {jobSource.display_name}
                    </a>
                  </h3>
                  {jobSource.corporate_name && (
                    <p className="text-muted small mb-0">
                      {jobSource.corporate_name}
                    </p>
                  )}
                  <div className="mt-1">
                    <a className="text-decoration-none" href={`${jobSource.url}/job_listings`}>
                      案件・求人数 {jobSource.job_listings_count.toLocaleString()} 件
                    </a>
                  </div>
                </div>
                
                  {/* ボタン部分 */}
                  <div className="d-flex flex-column gap-2 job-source-actions">
                    {jobSource.is_applied ? (
                      <button className="btn btn-sm btn-secondary w-100" disabled>
                        応募済み
                      </button>
                    ) : isUserSignedIn ? (
                      <form method="post" action={`/job_sources/${jobSource.id}/apply`} style={{ width: '100%' }} data-turbo="false">
                        <input type="hidden" name="authenticity_token" value={csrfToken} />
                        <button className="btn btn-sm btn-primary w-100" type="submit">
                          このエージェントに相談する
                        </button>
                      </form>
                    ) : (
                      <button
                        className="btn btn-sm btn-primary w-100"
                        onClick={(e) => {
                          e.preventDefault();
                          handleApplyClick(jobSource);
                        }}
                      >
                        このエージェントについて相談をする
                      </button>
                    )}
                    <a className="btn btn-sm btn-outline-primary w-100" href={jobSource.url}>
                      詳細を見る
                    </a>
                  </div>
              </div>
              
              {/* PC表示 */}
              <div className="d-none d-md-block mb-3">
                <div className="d-flex align-items-center gap-3">
                  {/* ロゴ部分 */}
                  <div className="flex-shrink-0 job-source-logo">
                    <a href={jobSource.url}>
                      {jobSource.logo_url ? (
                        <img
                          alt={`${jobSource.display_name}のロゴ`}
                          className="img-fluid border rounded"
                          style={{ maxHeight: '80px', padding: '4px' }}
                          loading="lazy"
                          decoding="async"
                          src={jobSource.logo_url}
                        />
                      ) : (
                        <div className="bg-light p-3 text-center border rounded">
                          <span className="text-muted">ロゴなし</span>
                        </div>
                      )}
                    </a>
                  </div>
                  
                  {/* サービス名部分 */}
                  <div className="flex-grow-1 min-w-0" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <h3 className="h4 mb-1 fw-bold">
                      <a className="text-decoration-none text-dark" style={{ wordBreak: 'break-word' }} href={jobSource.url}>
                        {jobSource.display_name}
                      </a>
                    </h3>
                    {jobSource.corporate_name && (
                      <p className="text-muted small mb-0" style={{ wordBreak: 'break-word' }}>
                        {jobSource.corporate_name}
                      </p>
                    )}
                    <div className="mt-1">
                      <a className="text-decoration-none" style={{ wordBreak: 'break-word' }} href={`${jobSource.url}/job_listings`}>
                        案件・求人数 {jobSource.job_listings_count.toLocaleString()} 件
                      </a>
                    </div>
                  </div>
                  
                  {/* ボタン部分 */}
                  <div className="flex-shrink-0 d-flex flex-column gap-2 job-source-actions" style={{ minWidth: '180px' }}>
                    {jobSource.is_applied ? (
                      <button className="btn btn-sm btn-secondary" style={{ whiteSpace: 'nowrap' }} disabled>
                        応募済み
                      </button>
                    ) : isUserSignedIn ? (
                      <form method="post" action={`/job_sources/${jobSource.id}/apply`} style={{ display: 'inline' }} data-turbo="false">
                        <input type="hidden" name="authenticity_token" value={csrfToken} />
                        <button className="btn btn-sm btn-primary" style={{ whiteSpace: 'nowrap' }} type="submit">
                          このエージェントに相談する
                        </button>
                      </form>
                    ) : (
                      <button
                        className="btn btn-sm btn-primary"
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleApplyClick(jobSource);
                        }}
                      >
                        このエージェントについて相談をする
                      </button>
                    )}
                    <a className="btn btn-sm btn-outline-primary" style={{ whiteSpace: 'nowrap' }} href={jobSource.url}>
                      詳細を見る
                    </a>
                  </div>
                </div>
              </div>
              
              {/* 詳細情報 */}
              <div>
                {(jobSource.payment_info || jobSource.contract_type_name) && (
                  <div className="bg-light rounded p-3 mb-3" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    {jobSource.payment_info && (
                      <p className="mb-2 mb-md-1" style={{ wordBreak: 'break-word' }}>
                        <strong>支払いサイト：</strong> {jobSource.payment_info}
                      </p>
                    )}
                    {jobSource.contract_type_name && (
                      <p className="mb-0" style={{ wordBreak: 'break-word' }}>
                        <strong>契約形態：</strong> {jobSource.contract_type_name}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 767.98px) {
          .job-source-logo img {
            width: 100% !important;
            max-width: 100% !important;
            max-height: none !important;
          }
          .job-source-actions .btn {
            padding: 0.875rem 1.5rem !important;
            font-size: 1rem !important;
            font-weight: 600 !important;
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FavoritedJobSourcesPage;

